import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Sparkles, UserPlus } from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(name, email, password);
    
    if (result.success) {
      navigate('/pulse');
    } else {
      setError(result.error || 'Registration failed');
    }
    
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      {/* Decorative Background Elements */}
      <div 
        className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20"
        style={{ backgroundColor: DESIGN.colors.primary, transform: 'translate(50%, -50%)' }}
      />
      <div 
        className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ backgroundColor: DESIGN.colors.purple, transform: 'translate(-50%, 50%)' }}
      />

      <div className="w-full max-w-md p-8 relative z-10">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              <Sparkles className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 
            className="text-3xl font-bold tracking-tight mb-1"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            CognitiveOS
          </h1>
          <p className="text-sm font-medium tracking-wider uppercase" style={{ color: DESIGN.colors.textMuted }}>
            Cognitive Execution Operating System
          </p>
        </div>

        {/* Register Card */}
        <div 
          className="rounded-2xl p-8"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            boxShadow: DESIGN.shadows.lg
          }}
        >
          <h2 
            className="text-xl font-semibold mb-6"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            Create your account
          </h2>

          {error && (
            <div 
              className="mb-5 p-4 rounded-xl flex items-start gap-3"
              style={{ backgroundColor: DESIGN.colors.errorSubtle }}
            >
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: DESIGN.colors.error }} />
              <p className="text-sm font-medium" style={{ color: DESIGN.colors.error }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label 
                htmlFor="name"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Full name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-2 h-11 rounded-xl"
                placeholder="Sarah Chen"
                style={{ 
                  borderColor: DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
              />
            </div>

            <div>
              <Label 
                htmlFor="email"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 rounded-xl"
                placeholder="you@company.com"
                style={{ 
                  borderColor: DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
              />
            </div>

            <div>
              <Label 
                htmlFor="password"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11 rounded-xl"
                placeholder="••••••••"
                style={{ 
                  borderColor: DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
              />
            </div>

            <div>
              <Label 
                htmlFor="confirmPassword"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Confirm password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-2 h-11 rounded-xl"
                placeholder="••••••••"
                style={{ 
                  borderColor: DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                color: 'white',
                boxShadow: '0 4px 12px rgba(0, 201, 167, 0.3)'
              }}
            >
              <UserPlus className="w-4 h-4" />
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-semibold transition-colors hover:opacity-80"
                style={{ color: DESIGN.colors.primary }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
